import React, { useState, useEffect } from 'react'
import {
    BrowserRouter,
    Switch,
    Route,
    Redirect,
    useLocation,
} from 'react-router-dom'
import {
    adminRoutes,
    apartmentRoutes,
    organizationRoutes,
    publicRoutes,
    vendorRoutes,
    manageRoutes,
} from './routes'
import { errorRoutes } from './routes/errorRoutes'
import type { PublicRouteConfig, RouteConfig } from './types'
import { useUser } from '../hooks'
import { LocalStorage } from '../helpers'
import { sharedRoutes } from './routes/sharedRoutes'
import { LoadingScreen } from './LoadingScreen'
import { AuthCallback } from '../main/containers/auth/AuthCallback/AuthCallback'
import { GuestCardLinkFormPage } from '../containers/Outbound/WebTools/GuestCardLinkFormPage'
import { GuestCardFormPage } from '../containers/Outbound/Campaign/GuestCards/GuestCardFormPage'
import { PublicLayout } from './layouts/PublicLayout'

const MINIMUM_LOADING_TIME = 500 // brief delay so the transition feels smooth, not a flash

const BodyBackgroundHandler: React.FC = () => null

function isAuthCallbackPath(pathname: string): boolean {
    return (
        pathname === '/auth-callback' || pathname.startsWith('/auth-callback/')
    )
}

function isPublicGuestCardPath(pathname: string): boolean {
    return (
        pathname.startsWith('/guest-card-link/') ||
        pathname.startsWith('/guest-card/')
    )
}

/** On auth-callback we render only AuthCallback so nothing can redirect or unmount. */
const RootNavigatorContent: React.FC = () => {
    const location = useLocation()
    if (isAuthCallbackPath(location.pathname)) return <AuthCallback />
    if (isPublicGuestCardPath(location.pathname))
        return <PublicGuestCardRoutes />
    return <MainRoutes />
}

const PublicGuestCardRoutes: React.FC = () => {
    return (
        <PublicLayout>
            <Switch>
                <Route
                    path="/guest-card-link/:token"
                    component={GuestCardLinkFormPage}
                />
                <Route
                    path="/guest-card/:token"
                    component={GuestCardFormPage}
                />
            </Switch>
        </PublicLayout>
    )
}

const MainRoutes: React.FC = () => {
    const {
        rootUser,
        workspaceUser,
        isLoading: isDataLoading,
        isError,
    } = useUser({
        shouldAuthenticate: true,
        shouldSetup: true,
    })

    const hasToken =
        typeof localStorage !== 'undefined' &&
        !!localStorage.getItem(LocalStorage.EZTURN4_TOKEN)
    // When no token (e.g. on /login), never show app LoadingScreen so the HTML loader isn't replaced by the same animation
    const [isLoading, setIsLoading] = useState(() => (hasToken ? true : false))
    const [startTime] = useState(Date.now())

    useEffect(() => {
        if (!hasToken) return
        if (!isDataLoading) {
            const elapsedTime = Date.now() - startTime
            const remainingTime = Math.max(
                0,
                MINIMUM_LOADING_TIME - elapsedTime,
            )

            const timer = setTimeout(() => {
                setIsLoading(false)
            }, remainingTime)

            return () => clearTimeout(timer)
        }
    }, [hasToken, isDataLoading, startTime])

    if (isLoading) {
        return <LoadingScreen />
    }

    if (isError) {
        return <div>Error loading user data</div>
    }

    const renderPublicRoutes = (routes: PublicRouteConfig[]) => {
        return routes.map((route) => {
            const Component = route.component
            const Layout = route.layout || React.Fragment
            const Guard = route.guard || React.Fragment

            return (
                <Route
                    key={route.path}
                    path={route.path}
                    exact={route.exact}
                    render={(props) => (
                        <Guard
                            {...(route.guard
                                ? { workspaceUser, rootUser }
                                : {})}
                        >
                            <Layout>
                                <Component {...props} />
                            </Layout>
                        </Guard>
                    )}
                />
            )
        })
    }

    const renderRoutes = (routes: RouteConfig[]) => {
        return routes.map((route) => {
            const Component = route.component
            const Layout = route.layout
            const Guard = route.guard || React.Fragment

            return (
                <Route
                    key={route.path}
                    path={route.path}
                    exact={route.exact}
                    render={(props) => (
                        <Guard
                            {...(route.guard
                                ? { workspaceUser, rootUser }
                                : {})}
                        >
                            <Layout user={rootUser!}>
                                <Component {...props} />
                            </Layout>
                        </Guard>
                    )}
                />
            )
        })
    }

    return (
        <>
            <BodyBackgroundHandler />
            <Switch>
                {/* Public routes */}
                {renderPublicRoutes(publicRoutes)}

                {/* Admin routes */}
                {renderRoutes(adminRoutes)}

                {/* Apartment routes */}
                {renderRoutes(apartmentRoutes)}

                {/* Organization routes */}
                {renderRoutes(organizationRoutes)}

                {/* Vendor routes */}
                {renderRoutes(vendorRoutes)}

                {/* Shared routes */}
                {renderRoutes(sharedRoutes)}

                {/* Organization routes */}
                {renderRoutes(organizationRoutes)}
                {/* Manage routes */}
                {renderRoutes(manageRoutes)}

                {/* Error / access denied routes */}
                {renderRoutes(errorRoutes)}

                {/* Fallback route */}
                <Route path="*" render={() => <Redirect to="/login" />} />
            </Switch>
        </>
    )
}

const Router = BrowserRouter as React.ComponentType<{
    children?: React.ReactNode
}>

export const RootNavigator: React.FC = () => (
    <Router>
        <RootNavigatorContent />
    </Router>
)
